// shared/ui/ConfirmDialog.jsx  (presentacional, reutilizable). Confirmación de acciones destructivas.
import React from 'react';
import { Box, CircularProgress, Dialog, IconButton } from '@mui/material';
import env from '../../../../environment.js';

// Los colores de texto llevan !important: sirio.css fuerza `button { color: ... !important }`.
const pillSx = {
  minWidth: 110,
  height: 40,
  padding: '0 22px',
  borderRadius: '999px',
  fontSize: 15,
  fontWeight: 600,
  textTransform: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  '&:focus-visible': { outline: '2px solid #1F1F1F', outlineOffset: '2px' },
  '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
};

const confirmSx = {
  ...pillSx,
  background: '#fff',
  border: '1.5px solid #E5484D',
  color: '#E5484D !important',
  '&:hover:not(:disabled)': { background: '#FDECEC' },
};

const cancelSx = {
  ...pillSx,
  background: env.colors.secondary_200,
  border: '1.5px solid transparent',
  color: '#1F1F1F !important',
  '&:hover:not(:disabled)': { filter: 'brightness(0.95)' },
};

const closeSx = {
  position: 'absolute',
  top: 14,
  right: 14,
  width: 32,
  height: 32,
  padding: 0,
  background: 'transparent',
  border: 'none',
  // sirio.css fuerza `button { color: ... !important }`: hay que ganarle con !important.
  color: '#6B6B66 !important',
  '&:hover:not(:disabled)': { color: '#1F1F1F !important', background: 'transparent' },
  '&:focus-visible': { outline: '2px solid #1F1F1F', outlineOffset: '2px' },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
};

const VARIANTES = {
  danger: { fondo: '#FDECEC', icono: '#B42318' },
  warning: { fondo: '#FDEBD3', icono: '#7A4A12' },
};

const ConfirmDialog = ({
  variant = 'danger',
  open,
  title,
  description,
  confirmText = 'Eliminar',
  cancelText = 'Cancelar',
  loading = false,
  error = null,
  onConfirm,
  onCancel,
}) => {
  const colores = VARIANTES[variant] || VARIANTES.danger;
  const cancelRef = React.useRef(null);
  const handleClose = () => {
    if (!loading) onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      maxWidth={false}
      TransitionProps={{ onEntered: () => cancelRef.current && cancelRef.current.focus() }}
      PaperProps={{
        sx: { width: '100%', maxWidth: 480, margin: 2, borderRadius: '16px', padding: '28px', boxShadow: '0 12px 40px rgba(0,0,0,0.16)' },
      }}>
      <IconButton aria-label="Cerrar" onClick={handleClose} disabled={loading} sx={closeSx}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
          <path d="M1.5 1.5L12.5 12.5M12.5 1.5L1.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </IconButton>
      <Box
        aria-hidden="true"
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: colores.fondo,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}>
        <i className="fa fa-exclamation-triangle" style={{ color: colores.icono, fontSize: 26 }} />
      </Box>
      <Box id="confirm-dialog-title" component="h2" sx={{ m: 0, mb: 1, fontSize: 22, fontWeight: 700, color: '#1F1F1F', lineHeight: 1.3 }}>
        {title}
      </Box>
      <Box id="confirm-dialog-description" component="p" sx={{ m: 0, fontSize: 15, color: '#5F5F59', lineHeight: 1.5 }}>
        {description}
      </Box>
      {error && (
        <Box
          role="alert"
          sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', mt: 2, color: '#B42318', fontSize: 14, lineHeight: 1.4 }}>
          <i className="fa fa-exclamation-triangle" aria-hidden="true" style={{ marginTop: 2 }} />
          <span>{error}</span>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', mt: 3, flexWrap: 'wrap' }}>
        <Box component="button" type="button" disabled={loading} onClick={onConfirm} sx={confirmSx}>
          {loading && <CircularProgress size={14} color="inherit" />}
          {loading ? 'Eliminando…' : confirmText}
        </Box>
        <Box component="button" type="button" ref={cancelRef} disabled={loading} onClick={onCancel} sx={cancelSx}>
          {cancelText}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ConfirmDialog;
